import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../ChatEngine';
import { useIsMobile } from '../useIsMobile';
import ChatPanel from './ChatPanel';
import { IconSparkle } from './Icons';
import { ecrireDockPos, lireDockPos, oublierDockPos, type DockPos as Pos } from './dockPosition';

/** Marge conservée entre la fenêtre et le bord de l'écran. */
const MARGE = 8;

/**
 * Garde la fenêtre ENTIÈREMENT dans l'écran. On borne sur sa taille réelle, pas
 * sur une marge symbolique : une fenêtre à moitié sortie cache son contenu et,
 * si le navigateur rétrécit ensuite, devient inatteignable.
 */
function borner(p: Pos, el: HTMLElement | null): Pos {
  const l = el?.offsetWidth ?? 420;
  const h = el?.offsetHeight ?? 400;
  // `Math.max(MARGE, …)` en second : sur un écran plus petit que la fenêtre, la
  // borne haute passerait sous la borne basse et collerait la fenêtre au bord opposé.
  const xMax = Math.max(MARGE, window.innerWidth - l - MARGE);
  const yMax = Math.max(MARGE, window.innerHeight - h - MARGE);
  return {
    x: Math.min(Math.max(p.x, MARGE), xMax),
    y: Math.min(Math.max(p.y, MARGE), yMax),
  };
}

/**
 * Déplacement de la fenêtre par sa barre de titre (ticket #36).
 *
 * La barre est presque entièrement occupée par le bouton de titre : on ne peut
 * donc pas réserver une zone « poignée ». Le glissement démarre sur n'importe
 * quel point de la barre, et un seuil de 4 px distingue le clic du déplacement —
 * sans lui, ouvrir le sélecteur de conversation déplacerait la fenêtre d'un
 * pixel et inversement.
 */
function useDeplacement(actif: boolean) {
  const [pos, setPos] = useState<Pos | null>(() => lireDockPos());
  const ref = useRef<HTMLDivElement>(null);
  const bouge = useRef(false);
  const depart = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

  // Le viewport peut rétrécir après coup : on reborne plutôt que de laisser la
  // fenêtre hors de portée.
  useEffect(() => {
    if (!actif || !pos) return;
    const onResize = () => setPos((p) => (p ? borner(p, ref.current) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [actif, pos]);

  // Une conversation NEUVE repart de l'emplacement d'origine : `ChatEngine`
  // efface la position mémorisée et émet cet événement, la fenêtre étant déjà
  // montée (on n'ouvre pas un nouveau composant, on réutilise celui-ci).
  useEffect(() => {
    const onReset = () => setPos(null);
    window.addEventListener('stm-dock-pos-reset', onReset);
    return () => window.removeEventListener('stm-dock-pos-reset', onReset);
  }, []);

  // La taille de la fenêtre n'est connue qu'après le rendu : une position relue
  // du stockage doit être revérifiée contre l'écran courant.
  useEffect(() => {
    if (!actif || !pos) return;
    const borne = borner(pos, ref.current);
    if (borne.x !== pos.x || borne.y !== pos.y) setPos(borne);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (!actif || e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    depart.current = { px: e.clientX, py: e.clientY, x: r.left, y: r.top };
    bouge.current = false;

    const onMove = (ev: PointerEvent) => {
      const d = depart.current;
      if (!d) return;
      const dx = ev.clientX - d.px;
      const dy = ev.clientY - d.py;
      if (!bouge.current && Math.abs(dx) + Math.abs(dy) < 4) return;
      bouge.current = true;
      ev.preventDefault();
      setPos(borner({ x: d.x + dx, y: d.y + dy }, ref.current));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      depart.current = null;
      if (bouge.current) {
        setPos((p) => { if (p) ecrireDockPos(p); return p; });
      }
      // Laisser le clic en cours se terminer avant de rouvrir les boutons.
      setTimeout(() => { bouge.current = false; }, 0);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [actif]);

  /** Double-clic sur la barre : retour au coin d'origine. */
  const reinitialiser = useCallback(() => {
    setPos(null);
    oublierDockPos();
  }, []);

  const style = actif && pos
    ? { left: pos.x, top: pos.y, right: 'auto' as const, bottom: 'auto' as const }
    : undefined;

  return { ref, style, onPointerDown, reinitialiser, aBougeRef: bouge };
}

/**
 * La conversation en bas à droite : même moteur que la page `/chat`, posée
 * par-dessus l'écran courant pour qu'on puisse continuer à naviguer.
 * Trois états : déployée, repliée sur sa barre de titre, fermée.
 * Sur mobile, elle occupe tout l'écran (une fenêtre de 380 px n'y tient pas).
 */
export default function ChatDock() {
  const chat = useChat();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Sur mobile la fenêtre occupe tout l'écran : il n'y a nulle part où la déplacer.
  const deplacement = useDeplacement(!useIsMobile());

  // Fermer le sélecteur de conversation au clic ailleurs / à Échap.
  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (chat.dock !== 'open') setMenuOpen(false);
  }, [chat.dock]);

  if (chat.dock === 'closed') return null;

  const minimized = chat.dock === 'minimized';
  const title = chat.activeTitle || 'Nouvelle conversation';

  return (
    <div
      className={`chat-dock ${minimized ? 'minimized' : ''}`}
      role="dialog"
      aria-label="Assistant"
      ref={deplacement.ref}
      style={deplacement.style}
    >
      <div
        className="chat-dock-bar"
        onPointerDown={deplacement.onPointerDown}
        onDoubleClick={deplacement.reinitialiser}
        title="Glisser pour déplacer · double-clic pour replacer dans le coin"
      >
        <div className="chat-dock-conv" ref={menuRef}>
          <button
            type="button"
            className="chat-dock-title"
            onClick={() => {
              // Un glissement se termine par un clic : sans ce garde-fou, déplacer
              // la fenêtre ouvrirait aussi le sélecteur de conversation.
              if (deplacement.aBougeRef.current) return;
              if (minimized) chat.openDock(); else setMenuOpen((v) => !v);
            }}
            title={minimized ? 'Déployer la conversation' : 'Changer de conversation'}
          >
            <IconSparkle size={14} />
            <span className="chat-dock-title-text">{title}</span>
            {chat.dockUnread && minimized && <span className="chat-dock-dot" />}
            {!minimized && <span className="chat-dock-caret">▾</span>}
          </button>
          {menuOpen && (
            <div className="chat-dock-menu">
              <button
                type="button"
                className="chat-dock-menu-new"
                onClick={() => { chat.openDock({ fresh: true }); setMenuOpen(false); }}
              >
                + Nouvelle conversation
              </button>
              <div className="chat-dock-menu-list">
                {chat.convs.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className={`chat-dock-menu-item ${c.id === chat.activeId ? 'active' : ''}`}
                    onClick={() => { chat.openConv(c.id); setMenuOpen(false); }}
                  >
                    {c.title}
                  </button>
                ))}
              </div>
              {chat.totalConvs > chat.convs.length && (
                <button
                  type="button"
                  className="chat-dock-menu-all"
                  onClick={() => { chat.closeDock(); nav('/conversations'); }}
                >
                  Toutes les conversations ({chat.totalConvs}) →
                </button>
              )}
            </div>
          )}
        </div>
        <div className="chat-dock-actions">
          <button
            type="button"
            onClick={() => (minimized ? chat.openDock() : chat.minimizeDock())}
            title={minimized ? 'Déployer' : 'Réduire'}
            aria-label={minimized ? 'Déployer' : 'Réduire'}
          >
            {minimized ? '▴' : '–'}
          </button>
          <button
            type="button"
            onClick={() => nav('/chat')}
            title="Ouvrir en plein écran"
            aria-label="Ouvrir en plein écran"
          >
            ⤢
          </button>
          <button type="button" onClick={chat.closeDock} title="Fermer" aria-label="Fermer">×</button>
        </div>
      </div>
      {!minimized && (
        <div className="chat-dock-body">
          <ChatPanel maxLines={5} autoFocus />
        </div>
      )}
    </div>
  );
}
